import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, Switch, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import Rest from 'tools/rest';
import Preloader from 'components/preloader';

const buildSnapshot = (record) => ({
  title: record?.title || '',
  active: record?.active ?? true,
  position: record?.position ?? null,
  file_url: record?.file_url || null,
});

const SiteDocumentCard = ({ siteDocumentId, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formDocument, setFormDocument] = useState(buildSnapshot(null));
  const [initialSnapshot, setInitialSnapshot] = useState(buildSnapshot(null));
  const [selectedFile, setSelectedFile] = useState(null);

  const loadSiteDocument = () => {
    if (!siteDocumentId) {
      const snapshot = buildSnapshot(null);
      setFormDocument(snapshot);
      setInitialSnapshot(snapshot);
      setErrors({});
      setSelectedFile(null);
      return;
    }

    setLoading(true);
    Rest.get(`/api/v1/site_documents/${siteDocumentId}.json`)
      .then((response) => {
        const loaded = response.data.site_document;
        const snapshot = buildSnapshot(loaded);
        setFormDocument(snapshot);
        setInitialSnapshot(snapshot);
        setErrors({});
        setSelectedFile(null);
      })
      .catch(() => message.error('Ошибка загрузки документа'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSiteDocument();
  }, [siteDocumentId]);

  const hasChanges = useMemo(() => {
    const fileChanged = Boolean(selectedFile);
    return fileChanged || JSON.stringify(formDocument) !== JSON.stringify(initialSnapshot);
  }, [formDocument, initialSnapshot, selectedFile]);

  const handleSave = () => {
    const formData = new FormData();
    formData.append('site_document[title]', formDocument.title || '');
    formData.append('site_document[active]', formDocument.active ? 'true' : 'false');
    if (formDocument.position !== null && formDocument.position !== undefined) {
      formData.append('site_document[position]', String(formDocument.position));
    }
    if (selectedFile) {
      formData.append('site_document[file]', selectedFile);
    }

    setLoading(true);
    const request = siteDocumentId
      ? Rest.put(`/api/v1/site_documents/${siteDocumentId}.json`, formData)
      : Rest.post('/api/v1/site_documents.json', formData);

    request.then((response) => {
      const updated = response.data.site_document;
      const snapshot = buildSnapshot(updated);
      setFormDocument(snapshot);
      setInitialSnapshot(snapshot);
      setSelectedFile(null);
      setErrors({});
      message.success(siteDocumentId ? 'Документ обновлен' : 'Документ создан');
      if (onUpdated) onUpdated(updated);
    }).catch((e) => {
      setErrors(e?.response?.data?.site_document?.errors || {});
      message.error(siteDocumentId ? 'Ошибка обновления документа' : 'Ошибка создания документа');
    }).finally(() => setLoading(false));
  };

  return (
    <Preloader loading={loading}>
      <Form labelCol={{ span: 5 }} wrapperCol={{ span: 19 }}>
        <Form.Item
          label="Название"
          help={errors?.title && errors.title.join(', ')}
          validateStatus={errors?.title ? 'error' : ''}
        >
          <Input
            value={formDocument.title}
            onChange={(e) => setFormDocument((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Название документа"
          />
        </Form.Item>
        <Form.Item
          label="Файл"
          help={errors?.file && errors.file.join(', ')}
          validateStatus={errors?.file ? 'error' : ''}
          extra={formDocument.file_url ? <a href={formDocument.file_url} target="_blank" rel="noreferrer">Текущий файл</a> : null}
        >
          <Upload
            maxCount={1}
            beforeUpload={(file) => {
              setSelectedFile(file);
              return false;
            }}
            onRemove={() => setSelectedFile(null)}
            fileList={selectedFile ? [selectedFile] : []}
          >
            <Button icon={<UploadOutlined />}>Выбрать файл</Button>
          </Upload>
        </Form.Item>
        <Form.Item label="Позиция">
          <InputNumber
            min={1}
            value={formDocument.position}
            onChange={(value) => setFormDocument((prev) => ({ ...prev, position: value }))}
          />
        </Form.Item>
        <Form.Item label="Опубликован">
          <Switch
            checked={Boolean(formDocument.active)}
            onChange={(checked) => setFormDocument((prev) => ({ ...prev, active: checked }))}
          />
        </Form.Item>
        {hasChanges && (
          <Form.Item wrapperCol={{ offset: 5, span: 19 }}>
            <Button type="primary" onClick={handleSave}>
              Сохранить
            </Button>
          </Form.Item>
        )}
      </Form>
    </Preloader>
  );
};

export default SiteDocumentCard;
